/**
 * One editor per Item type.
 *
 * Each is a controlled component over an `ItemValue`. None of them saves: they report a proposed
 * value upward and the surface decides what to do with it, because **Save is an act** and an editor
 * must not be able to perform one as a side effect of typing.
 */

import { useTranslation } from 'react-i18next';
import type { ItemType, ItemValue } from '@pius/contracts';

import { OptionList } from './OptionList';
import { DEFERRED_ITEM_TYPES, type ItemEditor, type ItemEditorProps } from './types';
import './renderers.css';

// ── option-bearing types ────────────────────────────────────────────────────

export function SingleOptionEditor(props: ItemEditorProps): React.JSX.Element {
  const selected =
    props.value?.type === 'single_option'
      ? [props.value.option_key]
      : selectedFallback(props.value);

  return (
    <OptionList
      name={props.itemId}
      options={props.options}
      selected={selected}
      multiple={false}
      disabled={props.disabled}
      onToggle={(key) => props.onChange({ type: 'single_option', option_key: key })}
    />
  );
}

export function MultiOptionEditor(props: ItemEditorProps): React.JSX.Element {
  const selected = props.value?.type === 'multi_option' ? props.value.option_keys : [];
  const min = props.minSelections;
  const max = props.maxSelections;

  const toggle = (key: string): void => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : // The cap is enforced server-side too; refusing the click here only avoids a pointless
        // round trip, it is not what makes the rule hold.
        max !== undefined && selected.length >= max
        ? selected
        : [...selected, key];

    props.onChange(next.length === 0 ? null : { type: 'multi_option', option_keys: next });
  };

  return (
    <>
      <OptionList
        name={props.itemId}
        options={props.options}
        selected={selected}
        multiple
        disabled={props.disabled}
        onToggle={toggle}
      />
      {min !== undefined || max !== undefined ? (
        <p className="ren-hint">
          {min !== undefined && max !== undefined
            ? `Choose between ${min} and ${max}.`
            : min !== undefined
              ? `Choose at least ${min}.`
              : `Choose no more than ${max}.`}{' '}
          {selected.length} chosen.
        </p>
      ) : null}
    </>
  );
}

/**
 * A scripture citation.
 *
 * Two surfaces from one type: when the Template declares a lectionary set — as a wedding's readings
 * do — the options are the permitted pericopes and the Policy narrows them. When it declares none,
 * a citation is typed. The value shape is the same either way; only `lectionary_key` differs, and it
 * is what lets an export resolve the reading rather than reprint a string.
 */
export function ScriptureRefEditor(props: ItemEditorProps): React.JSX.Element {
  const value = props.value?.type === 'scripture_ref' ? props.value : null;

  if (props.options.length > 0) {
    return (
      <OptionList
        name={props.itemId}
        options={props.options}
        selected={value?.lectionary_key ? [value.lectionary_key] : []}
        multiple={false}
        disabled={props.disabled}
        onToggle={(key) => {
          const option = props.options.find((o) => o.key === key);
          props.onChange({
            type: 'scripture_ref',
            citation: option?.label ?? key,
            lectionary_key: key,
          });
        }}
      />
    );
  }

  return (
    <TextField
      label={props.label}
      placeholder="e.g. 1 Corinthians 12:31 — 13:8a"
      value={value?.citation ?? ''}
      disabled={props.disabled}
      onChange={(text) =>
        props.onChange(text.trim() ? { type: 'scripture_ref', citation: text } : null)
      }
    />
  );
}

export function MusicRefEditor(props: ItemEditorProps): React.JSX.Element {
  const value = props.value?.type === 'music_ref' ? props.value : null;

  if (props.options.length > 0) {
    return (
      <OptionList
        name={props.itemId}
        options={props.options}
        selected={value?.source_ref ? [value.source_ref] : []}
        multiple={false}
        disabled={props.disabled}
        onToggle={(key) => {
          const option = props.options.find((o) => o.key === key);
          props.onChange({ type: 'music_ref', title: option?.label ?? key, source_ref: key });
        }}
      />
    );
  }

  return (
    <div className="ren-pair">
      <TextField
        label="Title"
        value={value?.title ?? ''}
        disabled={props.disabled}
        onChange={(title) =>
          props.onChange(
            title.trim()
              ? {
                  type: 'music_ref',
                  title,
                  ...(value?.composer ? { composer: value.composer } : {}),
                }
              : null,
          )
        }
      />
      <TextField
        label="Composer"
        value={value?.composer ?? ''}
        disabled={props.disabled || !value?.title}
        onChange={(composer) =>
          value?.title
            ? props.onChange({ type: 'music_ref', title: value.title, composer })
            : undefined
        }
      />
    </div>
  );
}

// ── plain types ─────────────────────────────────────────────────────────────

export function FreeTextEditor(props: ItemEditorProps): React.JSX.Element {
  const text = props.value?.type === 'free_text' ? props.value.text : '';

  return (
    <label className="ren-field">
      <span className="visually-hidden">{props.label}</span>
      <textarea
        rows={3}
        value={text}
        disabled={props.disabled}
        placeholder="Enter a value"
        onChange={(e) =>
          props.onChange(e.target.value.trim() ? { type: 'free_text', text: e.target.value } : null)
        }
      />
    </label>
  );
}

export function YesNoEditor(props: ItemEditorProps): React.JSX.Element {
  const current = props.value?.type === 'yes_no' ? props.value.value : null;

  return (
    <div className="ren-options" role="radiogroup" aria-label={props.label}>
      {[
        { key: 'yes', label: 'Yes', value: true },
        { key: 'no', label: 'No', value: false },
      ].map((choice) => (
        <label key={choice.key} className="ren-option">
          <input
            type="radio"
            name={props.itemId}
            checked={current === choice.value}
            disabled={props.disabled}
            onChange={() => props.onChange({ type: 'yes_no', value: choice.value })}
          />
          <span className="ren-option__label">{choice.label}</span>
        </label>
      ))}
      {/* Unset is a third state, and it is not "no". An optional item left unset is carried into
          the export as unset (docs/engineering/03-lifecycle-and-states.md). */}
      {current !== null && !props.disabled ? (
        <button type="button" className="ren-clear" onClick={() => props.onChange(null)}>
          Clear
        </button>
      ) : null}
    </div>
  );
}

export function NumberEditor(props: ItemEditorProps): React.JSX.Element {
  const current = props.value?.type === 'number' ? String(props.value.value) : '';

  return (
    <label className="ren-field ren-field--narrow">
      <span className="visually-hidden">{props.label}</span>
      <input
        type="number"
        step={1}
        min={0}
        value={current}
        disabled={props.disabled}
        onChange={(e) => {
          const parsed = Number.parseInt(e.target.value, 10);
          props.onChange(Number.isInteger(parsed) ? { type: 'number', value: parsed } : null);
        }}
      />
    </label>
  );
}

/**
 * A date and time **in the celebration's own timezone**.
 *
 * The timezone travels with the value rather than being inferred at render time. A parish secretary
 * planning from another country must not shift a nuptial Mass by six hours because their laptop
 * says so, and an export rendered on a server in a third timezone must print the local hour.
 */
export function DateTimeEditor(props: ItemEditorProps): React.JSX.Element {
  const value = props.value?.type === 'date_time' ? props.value : null;
  const timezone = value?.timezone ?? props.timezone ?? null;

  return (
    <div className="ren-pair">
      <label className="ren-field">
        <span className="visually-hidden">{props.label}</span>
        <input
          type="datetime-local"
          value={value?.local_datetime ?? ''}
          disabled={props.disabled || !timezone}
          onChange={(e) =>
            props.onChange(
              e.target.value && timezone
                ? { type: 'date_time', local_datetime: e.target.value, timezone }
                : null,
            )
          }
        />
      </label>
      <p className="ren-hint">
        {timezone ? (
          <>Recorded in {timezone}, the celebration&rsquo;s own timezone.</>
        ) : (
          <>Set the celebration&rsquo;s timezone on the overview before recording a time.</>
        )}
      </p>
    </div>
  );
}

/**
 * A named person filling a Role.
 *
 * Chosen from this celebration's People rather than typed, so the recorded `person_id` always
 * resolves to someone the roster holds. A free-text name would produce values nothing else in the
 * system could join to.
 *
 * **Choosing someone here grants them nothing.** It records who serves; authority comes only from a
 * Capability (docs/engineering/04-authorization.md).
 */
export function PersonRefEditor(props: ItemEditorProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const value = props.value?.type === 'person_ref' ? props.value : null;
  const people = props.people ?? [];

  if (people.length === 0) {
    return (
      <p className="ren-deferred">
        Nobody has been added to this celebration yet. Add people on the People screen, then choose
        one here.
      </p>
    );
  }

  return (
    <>
      <label className="ren-field">
        <span className="visually-hidden">{props.label}</span>
        <select
          value={value?.person_id ?? ''}
          disabled={props.disabled}
          onChange={(e) => {
            const person = people.find((p) => p.id === e.target.value);
            props.onChange(
              person
                ? { type: 'person_ref', person_id: person.id, display_name: person.display_name }
                : null,
            );
          }}
        >
          <option value="">{t('people.nobody_chosen')}</option>
          {people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.display_name}
            </option>
          ))}
        </select>
      </label>
      <p className="ren-hint">
        Recording who serves. It grants no authority in the workspace — that comes only from a
        Capability.
      </p>
    </>
  );
}

/**
 * A reference to an uploaded file.
 *
 * **Only available files are offered.** A quarantined attachment appears in the list, named and
 * unselectable, so a planner can see that the file exists and is still being checked — but it
 * cannot be chosen, because referencing it from a Planning Item would carry an unavailable file
 * into an approved value and from there into an export
 * (docs/engineering/03-lifecycle-and-states.md).
 */
export function AttachmentRefEditor(props: ItemEditorProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const value = props.value?.type === 'attachment_ref' ? props.value : null;
  const attachments = props.attachments ?? [];

  if (attachments.length === 0) {
    return (
      <p className="ren-deferred">
        Nothing has been attached to this celebration yet. Upload a file on the Attachments screen,
        then reference it here once the malware scan clears it.
      </p>
    );
  }

  return (
    <>
      <label className="ren-field">
        <span className="visually-hidden">{props.label}</span>
        <select
          value={value?.attachment_id ?? ''}
          disabled={props.disabled}
          onChange={(e) =>
            props.onChange(
              e.target.value ? { type: 'attachment_ref', attachment_id: e.target.value } : null,
            )
          }
        >
          <option value="">{t('attachments.no_file_chosen')}</option>
          {attachments.map((file) => (
            <option key={file.id} value={file.id} disabled={!file.available}>
              {file.display_name}
              {file.available ? '' : ' — not available yet'}
            </option>
          ))}
        </select>
      </label>
      <p className="ren-hint">
        A file becomes selectable once the malware scan clears it. Until then it is listed but
        cannot be chosen.
      </p>
    </>
  );
}

// ── types whose surface belongs to a later stage ────────────────────────────

/**
 * Not an editor: a statement that this Item cannot be edited here yet.
 *
 * Rendering a text box that writes a value the domain cannot yet hold would be worse than rendering
 * nothing — it would collect answers the roster or the file store will not accept.
 */
export function deferredEditor(itemType: ItemType): ItemEditor {
  const explanation = DEFERRED_ITEM_TYPES[itemType];

  return function DeferredEditor(): React.JSX.Element {
    return (
      <p className="ren-deferred">
        {explanation ?? 'This item type cannot be edited here yet.'} Anything already recorded is
        preserved and shown above.
      </p>
    );
  };
}

// ── shared field ────────────────────────────────────────────────────────────

function TextField({
  label,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  disabled: boolean;
  onChange: (next: string) => void;
}): React.JSX.Element {
  return (
    <label className="ren-field">
      <span className="ren-field__label">{label}</span>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/**
 * A selected option carried over from a value of a different shape.
 *
 * Returns nothing rather than guessing. A `free_text` value on an item that is now `single_option`
 * is a real situation — a Template Version can change an Item's type — and inventing a match would
 * silently rewrite what someone chose.
 */
function selectedFallback(value: ItemValue | null): string[] {
  return value?.type === 'multi_option' ? value.option_keys.slice(0, 1) : [];
}
